import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Progress } from '@/components/ui/progress';
import { Download, FileArchive } from 'lucide-react';
import { useFaviconGenerator, ICON_PRESETS, type FaviconOptions } from '@/hooks/useFaviconGenerator';
import type { UploadedImage } from '@/pages/ImageToolsHub';
import { toast } from 'sonner';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';

interface FaviconGeneratorToolProps {
  images: UploadedImage[];
}

export const FaviconGeneratorTool: React.FC<FaviconGeneratorToolProps> = ({ images }) => {
  const { isProcessing, generatedIcons, generateIcons, downloadIcon, downloadAsZip, downloadAsIco } = useFaviconGenerator();
  
  const [options, setOptions] = useState<FaviconOptions>({
    sizes: ICON_PRESETS.favicon,
    addPadding: true,
    paddingPercent: 10,
    backgroundColor: '#FFFFFF',
    roundCorners: false,
    cornerRadius: 20,
  });

  const [selectedPreset, setSelectedPreset] = useState<'favicon' | 'ios' | 'android' | 'all' | 'custom'>('favicon');

  const selectedImage = images.find(img => img.selected);

  const handlePresetChange = (preset: 'favicon' | 'ios' | 'android' | 'all' | 'custom') => {
    setSelectedPreset(preset);
    if (preset !== 'custom') {
      setOptions({ ...options, sizes: ICON_PRESETS[preset] });
    }
  };

  const toggleSize = (size: number) => {
    setOptions(prev => ({
      ...prev,
      sizes: prev.sizes.includes(size)
        ? prev.sizes.filter(s => s !== size)
        : [...prev.sizes, size].sort((a, b) => a - b)
    }));
  };

  const handleProcess = async () => {
    if (!selectedImage) {
      toast.error('Please select one image');
      return;
    }

    if (options.sizes.length === 0) {
      toast.error('Please select at least one size');
      return;
    }

    await generateIcons(selectedImage.preview, options);
    toast.success(`Generated ${options.sizes.length} icon size(s)`);
  };

  const handleDownloadZip = () => {
    downloadAsZip(generatedIcons);
    toast.success('Downloaded icons as ZIP');
  };

  const handleDownloadIco = () => {
    downloadAsIco(generatedIcons);
    toast.success('Downloaded as favicon.ico');
  };

  const commonSizes = [16, 32, 48, 72, 96, 120, 144, 152, 167, 180, 192, 512];

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Favicon & Icon Generator</CardTitle>
          <CardDescription>
            Generate app icons and favicons in multiple sizes
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {!selectedImage && (
            <div className="p-4 border border-yellow-500 bg-yellow-500/10 rounded-lg">
              <p className="text-sm text-yellow-600 dark:text-yellow-400">
                Please select exactly one image from the library above
              </p>
            </div>
          )}

          {/* Preset Selection */}
          <div className="space-y-2">
            <Label>Icon Preset</Label>
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant={selectedPreset === 'favicon' ? 'default' : 'outline'}
                onClick={() => handlePresetChange('favicon')}
              >
                Favicon Only
              </Button>
              <Button
                variant={selectedPreset === 'ios' ? 'default' : 'outline'}
                onClick={() => handlePresetChange('ios')}
              >
                iOS Icons
              </Button>
              <Button
                variant={selectedPreset === 'android' ? 'default' : 'outline'}
                onClick={() => handlePresetChange('android')}
              >
                Android Icons
              </Button>
              <Button
                variant={selectedPreset === 'all' ? 'default' : 'outline'}
                onClick={() => handlePresetChange('all')}
              >
                All Sizes
              </Button>
            </div>
          </div>

          {/* Custom Size Selection */}
          <div className="space-y-2">
            <Label>Custom Sizes</Label>
            <div className="grid grid-cols-4 gap-2">
              {commonSizes.map(size => (
                <div key={size} className="flex items-center space-x-2">
                  <Checkbox
                    id={`size-${size}`}
                    checked={options.sizes.includes(size)}
                    onCheckedChange={() => toggleSize(size)}
                  />
                  <Label htmlFor={`size-${size}`} className="text-sm cursor-pointer">
                    {size}px
                  </Label>
                </div>
              ))}
            </div>
          </div>

          {/* Padding */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <Label>Add Padding</Label>
              <Switch
                checked={options.addPadding}
                onCheckedChange={(checked) => setOptions({ ...options, addPadding: checked })}
              />
            </div>

            {options.addPadding && (
              <div className="space-y-2">
                <Label>Padding: {options.paddingPercent}%</Label>
                <Slider
                  value={[options.paddingPercent]}
                  onValueChange={([value]) => setOptions({ ...options, paddingPercent: value })}
                  min={0}
                  max={30}
                  step={1}
                />
              </div>
            )}
          </div>

          {/* Background Color */}
          <div className="space-y-2">
            <Label>Background Color</Label>
            <div className="flex gap-2">
              <Input
                type="color"
                value={options.backgroundColor}
                onChange={(e) => setOptions({ ...options, backgroundColor: e.target.value })}
                className="w-20 h-10"
              />
              <Input
                type="text"
                value={options.backgroundColor}
                onChange={(e) => setOptions({ ...options, backgroundColor: e.target.value })}
                placeholder="#FFFFFF"
              />
            </div>
          </div>

          {/* Round Corners */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <Label>Round Corners (iOS Style)</Label>
                <p className="text-xs text-muted-foreground">Recommended for iOS icons</p>
              </div>
              <Switch
                checked={options.roundCorners}
                onCheckedChange={(checked) => setOptions({ ...options, roundCorners: checked })}
              />
            </div>

            {options.roundCorners && (
              <div className="space-y-2">
                <Label>Corner Radius: {options.cornerRadius}%</Label>
                <Slider
                  value={[options.cornerRadius]}
                  onValueChange={([value]) => setOptions({ ...options, cornerRadius: value })}
                  min={0}
                  max={50}
                  step={1}
                />
              </div>
            )}
          </div>

          {/* Process Button */}
          <Button
            onClick={handleProcess}
            disabled={isProcessing || !selectedImage || options.sizes.length === 0}
            className="w-full"
          >
            {isProcessing ? 'Generating...' : `Generate ${options.sizes.length} Icon(s)`}
          </Button>
        </CardContent>
      </Card>

      {/* Results */}
      {generatedIcons.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Generated Icons</CardTitle>
                <CardDescription>{generatedIcons.length} sizes created</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleDownloadIco} variant="outline">
                  <Download className="h-4 w-4 mr-2" />
                  .ICO
                </Button>
                <Button onClick={handleDownloadZip}>
                  <FileArchive className="h-4 w-4 mr-2" />
                  Download ZIP
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="grid grid-cols-4 md:grid-cols-6 gap-4">
            {generatedIcons.map((icon) => (
              <div
                key={icon.size}
                className="flex flex-col items-center gap-2 p-2 border rounded hover:bg-accent cursor-pointer"
                onClick={() => downloadIcon(icon)}
              >
                <img
                  src={icon.dataUrl}
                  alt={`${icon.size}x${icon.size}`}
                  className="w-full h-auto"
                  style={{ imageRendering: icon.size < 32 ? 'pixelated' : 'auto' }}
                />
                <span className="text-xs text-muted-foreground">{icon.size}px</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
